import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  Link,
  TextField,
  Typography,
} from '@mui/material';

import { isValidEmail } from '~/modules/helpers';

import { login, userPageLoaded } from '~/actions';
import { STATUS } from '~/literals';

import { LoginFormValidationMessages, LoginParams, RootState } from '~/types';

interface LoginFormProps {
  onSignUpClicked: (event: React.MouseEvent<HTMLElement>) => void;
  onForgotPasswordClicked: (event: React.MouseEvent<HTMLElement>) => void;
}

function Login({ onForgotPasswordClicked, onSignUpClicked }: LoginFormProps) {
  const dispatch = useDispatch();
  const status = useSelector<RootState, string>(({ user }) => user?.status || STATUS.IDLE);
  const errorMessage = useSelector<RootState, string>(({ user }) => user?.errorMessage || '');

  const [validationMode, setValidationMode] = useState(false);
  const [loginParams, setLoginParams] = useState<LoginParams>({
    email: '',
    password: '',
    rememberMe: false,
  });

  const [validationMessages, setValidationMessages] = useState<LoginFormValidationMessages>({
    email: [],
    password: [],
  });

  useEffect(() => {
    dispatch(userPageLoaded());
  }, []);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLoginParams = {
      ...loginParams,
      email: event.target.value,
    } as LoginParams;

    setLoginParams(newLoginParams);
    setValidationMessages(getSearchValidationMessages(newLoginParams));
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLoginParams = {
      ...loginParams,
      password: event.target.value,
    } as LoginParams;

    setLoginParams(newLoginParams);
    setValidationMessages(getSearchValidationMessages(newLoginParams));
  };

  const handleRememberMeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLoginParams = {
      ...loginParams,
      rememberMe: event.target.checked,
    } as LoginParams;

    setLoginParams(newLoginParams);
    setValidationMessages(getSearchValidationMessages(newLoginParams));
  };

  const handleSubmitLogin = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const messages = getSearchValidationMessages(loginParams);

    setValidationMessages(messages);

    if (messages.email.length || messages.password.length) {
      setValidationMode(true);
    } else {
      dispatch(login(loginParams));
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Sign in
        </Typography>

        <Box component="form" noValidate onSubmit={handleSubmitLogin} sx={{ mt: 1 }}>
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : ''}
          <TextField
            autoComplete="email"
            autoFocus
            error={validationMode && validationMessages.email.length > 0}
            fullWidth
            helperText={
              validationMode && validationMessages.email.length > 0
                ? validationMessages.email.join('\n')
                : null
            }
            id="email"
            label="Email Address"
            margin="normal"
            name="email"
            onChange={handleEmailChange}
            placeholder="Email Address"
            required
            type="email"
          />
          <TextField
            autoComplete="current-password"
            error={validationMode && validationMessages.password.length > 0}
            fullWidth
            helperText={
              validationMode && validationMessages.password.length > 0
                ? validationMessages.password.join('\n')
                : null
            }
            id="password"
            label="Password"
            margin="normal"
            name="password"
            onChange={handlePasswordChange}
            placeholder="Password"
            required
            type="password"
          />
          <FormControlLabel
            control={
              <Checkbox color="primary" onChange={handleRememberMeChange} value="remember" />
            }
            label="Remember me"
          />
          <Button
            disabled={status === STATUS.RUNNING}
            fullWidth
            sx={{ mt: 3, mb: 2 }}
            type="submit"
            variant="contained"
          >
            {status === STATUS.RUNNING ? <CircularProgress color="primary" size={24} /> : 'Login'}
          </Button>

          <Grid container>
            <Grid item xs>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link onClick={onForgotPasswordClicked} sx={{ cursor: 'pointer' }} variant="body2">
                Forgot password?
              </Link>
            </Grid>
            <Grid item>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link onClick={onSignUpClicked} sx={{ cursor: 'pointer' }} variant="body2">
                Don't have an account? Sign Up
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
}

export default Login;

const getSearchValidationMessages = (fields: LoginParams): LoginFormValidationMessages => {
  const validationMessages: LoginFormValidationMessages = {
    password: [],
    email: [],
  };

  if (!fields.password) {
    validationMessages.password.push('* Please enter password to login.');
  }

  if (!fields.email || !isValidEmail(fields.email)) {
    validationMessages.email.push('* Please enter valid email address.');
  }

  return validationMessages;
};
